import { useState, useCallback } from 'react';
import { ServiceRegistry } from '@/services/ServiceRegistry';

interface KycError {
  error_code: string;
  message: string;
  details?: {
    companyId: string;
    provider: string;
    type: string;
  };
}

interface UseKycErrorHandlerReturn {
  showKycModal: boolean;
  companyId: string | null;
  handleKycError: (error: unknown) => boolean;
  closeKycModal: () => void;
  handleKycCompleted: () => void;
}

/**
 * Hook to handle KYC_REQUIRED errors and show the KYC modal
 */
export const useKycErrorHandler = (): UseKycErrorHandlerReturn => {
  const [showKycModal, setShowKycModal] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  /**
   * Handle KYC errors from API responses
   * @param error The error object from API response
   * @returns true if it was a KYC error and was handled, false otherwise
   */
  const handleKycError = useCallback((error: unknown): boolean => {
    if (error instanceof Error) {
      // Check if message contains JSON with error_code
      try {
        const parsedMessage = JSON.parse(error.message);
        console.log('Parsed error message:', parsedMessage);
        if (parsedMessage.error_code === 'KYC_REQUIRED') {
          console.log('KYC error found in message:', parsedMessage);
          const extractedCompanyId = parsedMessage.details?.companyId;
          if (extractedCompanyId) {
            setCompanyId(extractedCompanyId);
            setShowKycModal(true);
            return true;
          }
        }
      } catch {
        // Message is not JSON, continue with other checks
      }
    }

    // Check if it's a Response object (should be handled by API client now)
    if (error && typeof error === 'object' && (error as any).status && (error as any).json) {
      return false; // Let the API client handle Response objects
    }

    if (error && typeof error === 'object') {
      for (const key in error) {
        console.log(`  ${key}:`, (error as any)[key]);
      }
    }

    // Check if it's a KYC_REQUIRED error (following the same pattern as CONSENT_REQUIRED)
    if (
      (error as any)?.error_code === 'KYC_REQUIRED' ||
      (error as any)?.message === 'KYC_REQUIRED' ||
      (typeof error === 'object' && error && (error as any)?.details?.companyId)
    ) {
      console.log('KYC required error detected:', error);

      // Extract company ID from error details
      const extractedCompanyId = (error as any).details?.companyId || (error as any).companyId;

      if (extractedCompanyId) {
        // Stack the KYC dialog on top of the caller's own modal instead of closing it first
        // (matching BankConnectTrigger.tsx's already-working nested-dialog pattern) — closing
        // the parent here previously wiped its in-progress state (e.g. ConfirmPaymentModal's
        // currentStep/currentGroupId/paymentGroups), stranding the user with no way to resume
        // where they left off after completing KYC (issue #1020).
        setCompanyId(extractedCompanyId);
        setShowKycModal(true);
        return true;
      } else {
        console.warn('KYC error detected but no company ID found in error details');
      }
    }

    return false;
  }, []);

  /**
   * Close the KYC modal
   */
  const closeKycModal = useCallback(() => {
    setShowKycModal(false);
    setCompanyId(null);
  }, []);

  /**
   * Handle KYC completion - close modal and clear state
   */
  const handleKycCompleted = useCallback(() => {
    setShowKycModal(false);
    setCompanyId(null);

    // Optionally trigger a refresh of the page or relevant data
    // This could be customized based on the specific use case
    console.log('KYC completed successfully');
  }, []);

  return {
    showKycModal,
    companyId,
    handleKycError,
    closeKycModal,
    handleKycCompleted
  };
};

/**
 * Utility function to check if an error is a KYC required error
 */
export const isKycRequiredError = (error: unknown): boolean => {
  return (
    (error as any)?.error_code === 'KYC_REQUIRED' ||
    (error as any)?.errorType === 'KYC_REQUIRED' ||
    (typeof error === 'object' && error && (error as any)?.details?.companyId)
  );
};

/**
 * Utility function to extract company ID from a KYC error
 */
export const extractCompanyIdFromKycError = (error: unknown): string | null => {
  if (isKycRequiredError(error)) {
    return (error as any).details?.companyId || (error as any).companyId || null;
  }
  return null;
};